'use client';

import { useRef, useState, useCallback, useLayoutEffect, type Key } from 'react';
import {
    ToggleButtonGroup as AriaToggleButtonGroup,
    type ToggleButtonGroupProps as AriaToggleButtonGroupProps
} from 'react-aria-components/ToggleButtonGroup';
import { cn } from '../../utils/cn';
import ToggleButton, { type ToggleButtonProps } from '../ToggleButton/ToggleButton.tsx';
import { InputLabel, type InputWithLabelProps } from '../../internal/inputs/InputLabel.tsx';
import { InputHelpers, type InputWithHelpersProps } from '../../internal/inputs/InputHelpers.tsx';

export interface TogglePillGroupOption {
    value: string | number | boolean;
    label: string;
    'aria-label'?: string;
}

export interface TogglePillGroupProps
    extends InputWithLabelProps,
        InputWithHelpersProps,
        Omit<
            AriaToggleButtonGroupProps,
            'orientation' | 'children' | 'selectionMode' | 'defaultSelectedKeys' | 'onSelectionChange'
        > {
    className?: string;
    options?: TogglePillGroupOption[];
    children?: React.ReactNode;
    size?: ToggleButtonProps['size'];
    defaultSelectedKey?: string;
    onSelectionChange?: (key: string | null) => void;
}

interface IndicatorStyle {
    left: number;
    width: number;
}

export const TogglePillGroup = ({
    className,
    size = 'md',
    label,
    optionalLabel,
    hint,
    indications,
    options,
    children,
    isDisabled,
    isRequired = false,
    showRequiredIndicator,
    defaultSelectedKey,
    onSelectionChange,
    ...rest
}: TogglePillGroupProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [indicatorStyle, setIndicatorStyle] = useState<IndicatorStyle | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const updateIndicator = useCallback(
        (selectedKey: string | null) => {
            if (!containerRef.current || !selectedKey) {
                setIndicatorStyle(null);
                return;
            }

            const selectedButton = containerRef.current.querySelector(
                `[data-key="${selectedKey}"]`
            ) as HTMLElement | null;

            if (selectedButton) {
                const containerRect = containerRef.current.getBoundingClientRect();
                const buttonRect = selectedButton.getBoundingClientRect();

                setIndicatorStyle({
                    left: buttonRect.left - containerRect.left,
                    width: buttonRect.width
                });

                if (!isInitialized) {
                    setIsInitialized(true);
                }
            }
        },
        [isInitialized]
    );

    useLayoutEffect(() => {
        if (defaultSelectedKey) {
            updateIndicator(defaultSelectedKey);
        }
    }, [defaultSelectedKey, updateIndicator]);

    const handleSelectionChange = (keys: Set<Key>) => {
        const selectedKey = keys.size > 0 ? String([...keys][0]) : null;
        updateIndicator(selectedKey);

        if (onSelectionChange) {
            onSelectionChange(selectedKey);
        }
    };

    const renderPills = () => {
        if (!options || options.length === 0) {
            return children;
        }

        return options.map((option) => {
            const { value, label: optionLabel, 'aria-label': ariaLabel } = option;
            const id = String(value);

            return (
                <ToggleButton
                    key={id}
                    id={id}
                    data-key={id}
                    aria-label={ariaLabel}
                    isDisabled={isDisabled}
                    size={size}
                    pill
                >
                    {optionLabel}
                </ToggleButton>
            );
        });
    };

    return (
        <AriaToggleButtonGroup
            {...rest}
            selectionMode="single"
            isDisabled={isDisabled}
            orientation="horizontal"
            defaultSelectedKeys={defaultSelectedKey ? [defaultSelectedKey] : undefined}
            onSelectionChange={handleSelectionChange}
            className={cn('flex flex-col items-start gap-lr-spacing-100', className)}
        >
            <InputLabel
                label={label}
                optionalLabel={optionalLabel}
                isRequired={isRequired}
                showRequiredIndicator={showRequiredIndicator}
            />

            <div
                ref={containerRef}
                className={cn(
                    `
                        relative flex w-fit items-center gap-lr-toggle-pill-group rounded-lr-full
                        bg-lr-bg-button-toggle-inactive-default
                        [&_button]:relative [&_button]:z-10
                        [&_button:not([data-selected])]:hover:bg-transparent
                        [&_button[data-selected]]:bg-transparent [&_button[data-selected]]:shadow-none
                    `,
                    isDisabled &&
                        `
                            cursor-not-allowed
                            [&_button]:cursor-not-allowed
                        `
                )}
            >
                {!!indicatorStyle && (
                    <div
                        className={cn(
                            'absolute top-0 h-full rounded-lr-full bg-lr-bg-button-toggle-active-default',
                            isInitialized ? 'transition-all duration-200 ease-out' : ''
                        )}
                        style={{
                            left: indicatorStyle.left,
                            width: indicatorStyle.width
                        }}
                        aria-hidden="true"
                    />
                )}
                {renderPills()}
            </div>

            <InputHelpers hint={hint} indications={indications} wrapErrorInFieldError={false} />
        </AriaToggleButtonGroup>
    );
};

TogglePillGroup.displayName = 'TwTogglePillGroup';

export default TogglePillGroup;
